export const BLANK_STRING = '';

/**
 * Returns if the string is empty - null, undefined
 *
 * @param string
 */
export function isEmptyString(string: string | undefined): boolean {
    return !string || Object.keys(string).length === 0;
}

/**
 * Returns if the string is empty - null, undefined
 *
 * @param string
 */
export function isNotEmptyString(string: string | undefined): boolean {
    return !isEmptyString(string);
}

/**
 * Returns if the string is empty - null, undefined, '', {} or has only spaces
 *
 * @param string
 */
export function isBlankString(string: string | undefined): boolean {
    return !string || Object.keys(string).length === 0 || string === '' || string.trim() === '';
}

/**
 * Returns if the string is not empty - null, undefined, '', {} or has only spaces
 *
 * @param string
 */
export function isNotBlankString(string: string | undefined): boolean {
    return !isBlankString(string);
}

export function getStringIfNonEmptyOrNull(string: string | undefined): string {
    if (isNotBlankString(string)) {
        return string as string;
    }
    return BLANK_STRING;
}

export function replaceAll(str: string, find: string, replace: string): string {
    if (isNotEmptyString(str) && isNotEmptyString(find) && isNotEmptyString(find)) {
        return str.replace(new RegExp(escapeRegExp(find), 'g'), replace);
    }
    return BLANK_STRING;
}

export function escapeRegExp(string: string): string {
    return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

/**
 * Returns a unique string for key purposes, prefer this instead of using Math.random()
 */
export function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0,
            v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

export function removeQuotesIfPresent(str: string) {
    return replaceAll(str, '"', '');
}

/**
 * Adds a padding to string with '"' if its missing
 * @param str
 */
export function addQuotesPaddingToStringIfMissing(str: string): string {
    return JSON.stringify(removeQuotesIfPresent(str));
}

/**
 * Converts a UTC date string (MM/DD/YYYY HH:mm:ss) to local timezone
 * The input is assumed to be in UTC and will be converted to the user's local timezone
 * Uses the user's browser locale for formatting
 * 
 * @param utcDateString - Date string in format "MM/DD/YYYY HH:mm:ss" (UTC)
 * @returns Formatted local date string using user's locale
 * 
 * @example
 * // If user is in IST (UTC+5:30) with en-IN locale
 * convertUTCToLocalTime("03/11/2026 10:47:41") // Returns "11/03/2026 16:17:41"
 * // If user is in PST (UTC-8) with en-US locale
 * convertUTCToLocalTime("03/11/2026 10:47:41") // Returns "03/11/2026 02:47:41"
 */
export function convertUTCToLocalTime(utcDateString: string | undefined): string {
    if (isBlankString(utcDateString)) {
        return BLANK_STRING;
    }

    try {
        // Parse the UTC date string (MM/DD/YYYY HH:mm:ss)
        const [datePart, timePart] = (utcDateString as string).split(' ');
        const [month, day, year] = datePart.split('/');
        const [hours, minutes, seconds] = timePart.split(':');

        // Create a Date object treating the input as UTC
        const utcDate = new Date(Date.UTC(
            parseInt(year),
            parseInt(month) - 1, // Month is 0-indexed
            parseInt(day),
            parseInt(hours),
            parseInt(minutes),
            parseInt(seconds)
        ));

        // Check if the date is valid
        if (isNaN(utcDate.getTime())) {
            return utcDateString as string;
        }

        // Use toLocaleString with user's browser locale for formatting
        // undefined locale uses the browser's default locale
        return utcDate.toLocaleString(undefined, {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        }).replace(',', '');
    } catch (error) {
        // If parsing fails, return the original string
        console.error('Error converting UTC to local time:', error);
        return utcDateString as string;
    }
}

const TIMESTAMP_PLACEHOLDER = '—';
const UTC_FORMAT_REGEX = /^\d{2}\/\d{2}\/\d{4} \d{2}:\d{2}:\d{2}$/;

function pad2(n: number): string {
    return String(n).padStart(2, '0');
}

/**
 * Converts an epoch-millisecond value (as produced by the backend Smithy
 * model) into the `MM/DD/YYYY HH:mm:ss` UTC string that
 * `convertUTCToLocalTime` expects as input.
 *
 * Callers MUST pass a finite number; `formatTimestamp` checks upstream.
 */
function epochMsToUtcString(n: number): string {
    const d = new Date(n);
    const mm = pad2(d.getUTCMonth() + 1);
    const dd = pad2(d.getUTCDate());
    const yyyy = String(d.getUTCFullYear()).padStart(4, '0');
    const hh = pad2(d.getUTCHours());
    const min = pad2(d.getUTCMinutes());
    const ss = pad2(d.getUTCSeconds());
    return `${mm}/${dd}/${yyyy} ${hh}:${min}:${ss}`;
}

/**
 * Normalises a backend timestamp value into a user-locale wall-clock
 * string by delegating to `convertUTCToLocalTime`. Returns the em-dash
 * placeholder for null, undefined, non-finite numbers, blank strings,
 * and any thrown / invalid path.
 *
 * Input shapes accepted:
 *   - `null` / `undefined`                              → `"—"`
 *   - `number` (epoch milliseconds):
 *       - non-finite (`NaN`, `±Infinity`)               → `"—"`
 *       - finite → `convertUTCToLocalTime(...)`
 *   - `string`:
 *       - blank (empty / whitespace-only)               → `"—"`
 *       - already `MM/DD/YYYY HH:mm:ss` UTC             → delegate directly
 *       - otherwise parsed as ISO-8601 UTC, re-emitted
 *         in `MM/DD/YYYY HH:mm:ss`, then delegated
 */
export function formatTimestamp(input: number | string | null | undefined): string {
    if (input === null || input === undefined) {
        return TIMESTAMP_PLACEHOLDER;
    }

    try {
        if (typeof input === 'number') {
            if (!Number.isFinite(input)) {
                return TIMESTAMP_PLACEHOLDER;
            }
            return convertUTCToLocalTime(epochMsToUtcString(input));
        }

        if (typeof input === 'string') {
            if (isBlankString(input)) {
                return TIMESTAMP_PLACEHOLDER;
            }

            if (UTC_FORMAT_REGEX.test(input)) {
                return convertUTCToLocalTime(input);
            }

            const parsedMs = Date.parse(input);
            if (!Number.isFinite(parsedMs)) {
                return TIMESTAMP_PLACEHOLDER;
            }
            return convertUTCToLocalTime(epochMsToUtcString(parsedMs));
        }
    } catch {
        return TIMESTAMP_PLACEHOLDER;
    }

    return TIMESTAMP_PLACEHOLDER;
}
